"use client";

import { cn } from "@gigpro/ui/tailwind/cn";
import { addYears, subYears } from "date-fns";
import type * as React from "react";
import { type CustomComponents, DayPicker } from "react-day-picker";
import { ChevronDown } from "../../Icons";
import { buttonVariants } from "../Button";
import {
  SelectBaseTrigger,
  SelectContent,
  SelectItem,
  SelectPrimitive,
  type SelectProps,
  SelectRoot,
  SelectValue,
} from "../Select";

export type CalendarProps = React.ComponentProps<typeof DayPicker>;

const components: Partial<CustomComponents> = {
  Chevron: ({ orientation, className }) => (
    <ChevronDown className={cn("size-5", orientation === "left" ? "rotate-90" : "-rotate-90", className)} />
  ),
  Dropdown: ({ options, value, onChange, ...props }) => {
    return (
      <SelectRoot
        value={`${value}`}
        onValueChange={(newValue) => {
          const syntheticEvent = { target: { value: newValue } } as React.ChangeEvent<HTMLSelectElement>;
          onChange?.(syntheticEvent);
        }}
        {...(props as SelectProps)}
      >
        <SelectBaseTrigger className="group z-20 flex items-center gap-1 text-extraLarge font-semibold">
          <SelectValue />
          <SelectPrimitive.Icon asChild>
            <ChevronDown className="size-4 shrink-0 text-brand transition-transform group-data-[state=open]:rotate-180" />
          </SelectPrimitive.Icon>
        </SelectBaseTrigger>
        <SelectContent>
          {options?.map((option) => (
            <SelectItem key={option.value} value={`${option.value}`} disabled={option.disabled}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </SelectRoot>
    );
  },
};

const defaultStartMonth = subYears(new Date(), 50);
const defaultEndMonth = addYears(new Date(), 50);

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  startMonth = defaultStartMonth,
  endMonth = defaultEndMonth,
  ...props
}: CalendarProps) {
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      captionLayout="dropdown"
      className={cn("p-2 pb-1", className)}
      classNames={{
        months: "rdp-months relative",
        month: "rdp-month flex flex-col gap-2 w-full",
        dropdowns: "rdp-dropdowns flex gap-2 items-center justify-center",
        button_previous: cn(
          "rdp-button_previous",
          buttonVariants({ variant: "text" }),
          "absolute top-0 left-1 size-6 bg-transparent p-0 aria-disabled:text-gray aria-disabled:hover:bg-transparent",
        ),
        button_next: cn(
          "rdp-button_next",
          buttonVariants({ variant: "text" }),
          "absolute top-0 right-1 size-6 bg-transparent p-0 aria-disabled:text-gray aria-disabled:hover:bg-transparent",
        ),
        month_grid: "rdp-month_grid w-full border-collapse space-y-1",

        weekdays: "rdp-weekdays grid grid-cols-7 w-full place-items-center",
        weekday: "rdp-weekday text-black rounded-md w-9 font-semibold text-[0.8rem]",
        week: "rdp-week grid grid-cols-7 w-full justify-between place-items-center",

        day: cn(
          "rdp-day",
          buttonVariants({ variant: "text" }),
          "m-[2px] size-8 rounded-full bg-background p-0 hover:bg-tags-purple aria-selected:opacity-100",
        ),
        day_button: "rdp-day_button size-full",
        selected:
          "rdp-selected bg-brand text-white hover:bg-brand-hover focus:bg-primary focus:text-primary-foreground",
        today: "rdp-today ring-medium-gray/50 ring-1",
        outside:
          "rdp-outside bg-transparent text-medium-gray hover:bg-brand/10 aria-selected:bg-brand/20 aria-selected:text-medium-gray",
        disabled: "rdp-disabled text-medium-gray bg-transparent hover:bg-transparent",
        hidden: "rdp-hidden invisible",
        ...classNames,
      }}
      components={components}
      data-testid="datepicker-calendar"
      startMonth={startMonth}
      endMonth={endMonth}
      {...props}
    />
  );
}
Calendar.displayName = "Calendar";

export { Calendar };
